import Random from 'random-js'
import { SimplexNoise } from '../perlin-noise'
import BIOMES from './biomes'

export class NewTerrain {
  constructor({ size = { x: 10, y: 10, z: 1 }, seed, zoom = 40 }) {
    if (size.x < 3 || size.y < 3 || size.z === 0) {
      throw new Error('Invalid size. Map must be 3x3x1 at minimum.')
    }

    this.zoom = zoom
    this.seed = seed
    this.size = size

    this._initializeRandomizationEngine(seed)

    this.distributions = {
      perlinDistribution: Random.real(0, 1, true),
    }

    this.generators = {
      elevation: new SimplexNoise({
        random: () => this.distributions.perlinDistribution(this.elevationEngine),
      }),
      moisture: new SimplexNoise({
        random: () => this.distributions.perlinDistribution(this.moistureEngine),
      }),
    }

    this.terrain = this._generateTerrain()
  }

  _initializeRandomizationEngine(seed) {
    const elevationEngine = Random.engines.mt19937()
    const moistureEngine = Random.engines.mt19937()
    if (seed) {
      let seedHash
      if (typeof seed === 'string') {
        seedHash = this._generateStringHash(seed)
      }
      this.elevationEngine = elevationEngine.seed(seedHash ? seedHash : seed)
      this.moistureEngine = moistureEngine.seed(seedHash ? -seedHash : -seed)
    } else {
      const seed = Random.integer(-9007199254740992, 9007199254740992)(Random.engines.nativeMath)
      console.log('seed', seed)
      this.elevationEngine = elevationEngine.seed(seed)
      this.moistureEngine = moistureEngine.seed(-seed)
    }
  }

  _generateStringHash(str) {
    let hash = 0
    if (str.length === 0) return hash
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i)
      hash |= 0 // Convert to 32bit integer
    }
    return hash
  }

  _generateTerrain() {
    let terrain = new Map()
    for (let z = 0; z < this.size.z; z++) {
      for (let y = 0; y < this.size.y; y++) {
        for (let x = 0; x < this.size.x; x++) {
          terrain.set(hashCoordinate({ x, y, z }), {
            elevation: this.generators.elevation.noise(x / this.zoom, y / this.zoom),
            moisture: this.generators.moisture.noise(x / this.zoom, y / this.zoom),
          })
        }
      }
    }
    console.log(terrain.size)
    return terrain
  }
}

export function hashCoordinate({ x, y, z }) {
  if (`${x}${y}${z}`.includes('undefined')) {
    throw new Error(`Undefined value provided [${x},${y},${z}]`)
  }
  return `x${x}y${y}z${z}`
}

export function printMapToCanvas({ size, map, canvas }) {
  if (!size) throw new Error('Must specify the size of the array')
  if (!map) throw new Error('Must provide a terrain map for iteration')
  if (!canvas) throw new Error('Must provide a canvas element')

  const ctx = canvas.getContext('2d')
  for (let coordinate of coordinateIterator(size, map)) {
    const currentBlock = map.get(hashCoordinate(coordinate))
    ctx.fillStyle = getBiomeFromElevationAndMoisture(currentBlock).color
    ctx.fillRect(coordinate.x, coordinate.y, 1, 1)
  }
}

function getBiomeFromElevationAndMoisture({ elevation, moisture }) {
  if (elevation < -0.4) return BIOMES.OCEAN
  if (elevation < -0.5) return BIOMES.BEACH

  if (elevation > 0.8) {
    if (moisture < 0.1) return BIOMES.SCORCHED
    if (moisture < 0.2) return BIOMES.BARE
    if (moisture < 0.5) return BIOMES.TUNDRA
    return BIOMES.SNOW
  }

  if (elevation > 0.6) {
    if (moisture < 0.33) return BIOMES.TEMPERATE_DESERT
    if (moisture < 0.66) return BIOMES.SHRUBLAND
    return BIOMES.TAIGA
  }

  if (elevation > 0.4) {
    if (moisture < 0.16) return BIOMES.TEMPERATE_DESERT
    if (moisture < 0.5) return BIOMES.GRASSLAND
    if (moisture < 0.83) return BIOMES.TEMPERATE_DECIDUOUS_FOREST
    return BIOMES.TEMPERATE_RAIN_FOREST
  }

  if (moisture < 0.16) return BIOMES.SUBTROPICAL_DESERT
  if (moisture < 0.33) return BIOMES.GRASSLAND
  if (moisture < 0.66) return BIOMES.TROPICAL_SEASONAL_FOREST
  return BIOMES.TROPICAL_RAIN_FOREST
}

export function* coordinateIterator(size) {
  for (let z = 0; z < size.z; z++) {
    for (let y = 0; y < size.y; y++) {
      for (let x = 0; x < size.x; x++) {
        yield { x, y, z }
      }
    }
  }
}
